import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useAuth } from '../hooks/useAuth';
import {
  createUserDocument,
  createQuizSession,
  saveChatMessage,
  completeQuizSession,
  updateUserDisplayName,
  getChatMessages,
  updateUserEmail
} from '../services/sessionService';
import apiService from '../services/apiService';
import { validateEmail } from '../utils/emailValidation';
import {
  notifyQuizApiError,
  notifyProfileCreationError,
  notifyGeneralError
} from '../services/slackService';
import { analyticsService } from '../services/analyticsService';
import Lottie from 'lottie-react';
import TypingIndicator from './TypingIndicator';
import PingPongVideo from './PingPongVideo';
import './QuizPage.css';

// Haptic feedback utility - triggers vibration when supported
const triggerHapticFeedback = () => {
  if ('vibrate' in navigator) {
    // Short vibration pulse (50ms) for chat bubble feedback
    navigator.vibrate(50);
  }
};

function QuizPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, error: authError } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCommentary, setShowCommentary] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [freeTextInput, setFreeTextInput] = useState('');
  const [userName, setUserName] = useState('');
  const [sliderValue, setSliderValue] = useState(0);
  const [sliderTouched, setSliderTouched] = useState(false); // Track if user has interacted with slider
  const [groupedScores, setGroupedScores] = useState({}); // Track scores for grouped questions
  const [bucketSort, setBucketSort] = useState({
    currentItemIndex: 0,
    assignments: {}, // { bucketId: [itemIndex, itemIndex, ...] }
  });
  const [dragState, setDragState] = useState({
    isDragging: false,
    dragOverBucket: null,
    isDropping: false,
  });
  const [touchState, setTouchState] = useState({
    isTouching: false,
    touchStartX: null,
    touchStartY: null,
    currentX: null,
    currentY: null,
    currentBucket: null,
  });
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [hidePanelForFinal, setHidePanelForFinal] = useState(false); // Completely hide panel after final question
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false); // Hide answer panel during transitions
  const [waitingForContinue, setWaitingForContinue] = useState(false); // Pause on Did You Know cards
  const [answerSectionScroll, setAnswerSectionScroll] = useState({ hasScroll: false, atBottom: true }); // Track answer section scroll state
  const pendingNextQuestion = useRef(null); // Store next question data while waiting for Continue
  const conversationEndRef = useRef(null);
  const savedScrollPosition = useRef(null);
  const dragItemRef = useRef(null);
  const answerSectionRef = useRef(null);
  const [shouldCollapseHeader, setShouldCollapseHeader] = useState(false);

  // Analytics time tracking
  const quizStartTime = useRef(null);
  const questionStartTime = useRef(null);
  const milestonesReached = useRef({ '25%': false, '50%': false, '75%': false });

  // Detect if device supports touch (mobile/tablet)
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Detect when header should collapse:
  // 1. When keyboard is open (viewport shrinks significantly)
  // 2. When answer section takes up too much of the viewport
  // 3. When viewport is small
  useEffect(() => {
    const checkShouldCollapse = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const windowHeight = window.innerHeight;

      // Check if keyboard is open (viewport shrunk by more than 150px)
      const keyboardOpen = (windowHeight - viewportHeight) > 150;

      // Check if answer section is too tall (more than 35% of viewport)
      const answerSection = answerSectionRef.current;
      let answerSectionTooTall = false;
      if (answerSection && !showTypingIndicator && !showEmailCapture) {
        const answerHeight = answerSection.offsetHeight;
        answerSectionTooTall = (answerHeight / viewportHeight) > 0.35;
      }

      // Check if viewport is small (less than 700px)
      const smallViewport = viewportHeight < 700;

      // Collapse header if any condition is met
      setShouldCollapseHeader(keyboardOpen || answerSectionTooTall || (smallViewport && !showTypingIndicator));
    };

    // Check on mount and when relevant state changes
    checkShouldCollapse();

    // Also check after a delay to ensure DOM has updated
    const timer = setTimeout(checkShouldCollapse, 100);

    // Also check on viewport resize
    const handleResize = () => checkShouldCollapse();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }
    window.addEventListener('resize', handleResize);

    // Use ResizeObserver to detect answer section size changes
    let resizeObserver;
    if (answerSectionRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => checkShouldCollapse());
      resizeObserver.observe(answerSectionRef.current);
    }

    return () => {
      clearTimeout(timer);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [showTypingIndicator, showEmailCapture, currentQuestionIndex]);

  // Track answer section scroll state (for showing/hiding scroll indicators)
  useEffect(() => {
    const answerSection = answerSectionRef.current;
    if (!answerSection) return;

    const checkScrollState = () => {
      const hasScroll = answerSection.scrollHeight > answerSection.clientHeight;
      const atBottom = answerSection.scrollTop + answerSection.clientHeight >= answerSection.scrollHeight - 5;
      setAnswerSectionScroll({ hasScroll, atBottom });
    };

    // Check on mount and when question changes
    const timer = setTimeout(checkScrollState, 100);

    // Check on scroll
    answerSection.addEventListener('scroll', checkScrollState);

    // Use ResizeObserver to detect content size changes
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkScrollState);
      resizeObserver.observe(answerSection);
    }

    return () => {
      clearTimeout(timer);
      answerSection.removeEventListener('scroll', checkScrollState);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [currentQuestionIndex, waitingForContinue, isTransitioning]);

  // Scroll to position last message BOTTOM just ABOVE the answer panel TOP
  // Triggers on each conversation change - with 600ms delays between steps, no debounce needed
  useEffect(() => {
    if (showEmailCapture) return; // Skip during email capture (panel hidden)

    // Small delay to ensure DOM has updated
    const timer = setTimeout(() => {
      const conversationArea = document.querySelector('.conversation-area');
      const answerSection = answerSectionRef.current;
      if (!conversationArea || !answerSection) return;

      // Reset answer panel scroll to TOP when showing new options
      if (!showTypingIndicator) {
        answerSection.scrollTo({ top: 0, behavior: 'instant' });
      }

      // Find the last message bubble (AI or user)
      const allMessages = conversationArea.querySelectorAll('.message-bubble, .did-you-know-card');
      const lastMessage = allMessages[allMessages.length - 1];

      if (lastMessage) {
        const messageRect = lastMessage.getBoundingClientRect();
        const answerPanelTop = answerSection.getBoundingClientRect().top;

        // Target: Position message BOTTOM just ABOVE answer panel (with 16px padding)
        const targetBottom = answerPanelTop - 16;
        const currentBottom = messageRect.bottom;

        // Only scroll if message bottom is below target (would be hidden by panel)
        if (currentBottom > targetBottom) {
          const scrollDelta = currentBottom - targetBottom;
          const newScrollTop = conversationArea.scrollTop + scrollDelta;

          conversationArea.scrollTo({
            top: Math.max(0, newScrollTop),
            behavior: 'instant' // Instant scroll - no animation jerkiness
          });
        }
      }
    }, 50); // Small delay for DOM update

    return () => clearTimeout(timer);
  }, [conversationHistory, showTypingIndicator, showEmailCapture]);

  useEffect(() => {
    fetchQuestions();
  }, []);

  // Reset functionality: Check for ?reset=true query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldReset = params.get('reset') === 'true';

    if (shouldReset && user) {
      console.log('🔄 Reset requested - signing out and creating new anonymous user...');

      // Sign out current user
      signOut(auth)
        .then(() => {
          console.log('✅ Signed out successfully');
          // Remove reset parameter and reload to start fresh
          window.location.href = window.location.pathname;
        })
        .catch((error) => {
          console.error('❌ Error signing out:', error);
        });
    }
  }, [user]);

  // Support direct navigation via URL parameter (?startAt=13)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const startAt = params.get('startAt');
    if (startAt && questions.length > 0) {
      const questionIndex = parseInt(startAt) - 1; // Convert to 0-indexed
      if (questionIndex >= 0 && questionIndex < questions.length) {
        setCurrentQuestionIndex(questionIndex);
        const targetQuestion = questions[questionIndex];
        setConversationHistory([
          {
            type: 'ai',
            text: targetQuestion.question_text
          }
        ]);
      }
    }
  }, [questions]);

  // SYNCHRONOUS scroll restoration (runs BEFORE browser paint) - ONLY for bucket-sort
  useLayoutEffect(() => {
    if (showTypingIndicator && savedScrollPosition.current !== null) {
      const conversationArea = document.querySelector('.conversation-area');
      if (conversationArea) {
        console.log('📍 Restoring scroll position (BEFORE paint) for bucket-sort:', savedScrollPosition.current);
        conversationArea.scrollTop = savedScrollPosition.current;
        savedScrollPosition.current = null; // Clear after restoring
      }
    }
  }, [showTypingIndicator]);

  // Auto-scroll to show bottom of last element (message or answer section)
  useEffect(() => {
    console.log('🔍 Auto-scroll useEffect triggered', {
      showEmailCapture,
      showTypingIndicator,
      emailError,
      currentQuestionIndex
    });

    const conversationArea = document.querySelector('.conversation-area');
    if (!conversationArea) {
      console.log('⚠️ No conversation area found');
      return;
    }

    // Add delay to ensure content has rendered
    setTimeout(() => {
      if (!conversationArea) return;

      let targetElement;

      // PRIORITY ORDER: Email capture > Typing indicator > Answer section
      if (showEmailCapture) {
        // Email capture is showing - prioritize it over commentary
        // Target the INPUT (not send button) to position it higher for mobile keyboard visibility
        const emailCaptureContainer = conversationArea.querySelector('.email-capture-container');

        console.log('🔍 Email capture section check:', {
          hasEmailCapture: !!emailCaptureContainer,
          showEmailCapture
        });

        if (emailCaptureContainer) {
          // Target the email INPUT (not send button) to leave room for mobile keyboard
          const emailInput = emailCaptureContainer.querySelector('.free-text-input');

          // Always use input field for email capture to ensure keyboard doesn't cover it
          targetElement = emailInput || emailCaptureContainer;

          console.log('🎯 Email capture target found:', {
            type: 'email-capture',
            hasEmailInput: !!emailInput,
            targetClassName: targetElement?.className,
            targetingStrategy: 'input-priority-for-keyboard'
          });
        } else {
          console.log('⚠️ No email capture container found');
        }
      } else if (showTypingIndicator) {
        // Typing indicator is showing - scroll to show bottom of last message/card/indicator
        const messages = conversationArea.querySelectorAll('.message-bubble, .did-you-know-card, .typing-indicator-container');
        targetElement = messages[messages.length - 1];
        console.log('🎯 Target: Typing indicator', { messageCount: messages.length });
      } else {
        // Answer section is showing - DON'T scroll here
        // The question visibility scroll (earlier useEffect) handles positioning
        // Scrolling to the send button/options would push the question out of view
        console.log('🔍 Answer section showing - skipping scroll (question scroll handles this)');
        return; // Exit early - don't scroll
      }

      // Only scroll if content overflows (doesn't fit on screen)
      const contentOverflows = conversationArea.scrollHeight > conversationArea.clientHeight;

      console.log('📏 Overflow check:', {
        contentOverflows,
        scrollHeight: conversationArea.scrollHeight,
        clientHeight: conversationArea.clientHeight,
        hasTargetElement: !!targetElement
      });

      if (contentOverflows && targetElement) {
        const elementRect = targetElement.getBoundingClientRect();
        const containerRect = conversationArea.getBoundingClientRect();

        // For email capture, use stricter positioning - position at 1/3 from top (for mobile keyboard)
        // For other elements, use standard visibility check (show bottom on screen)
        let needsScroll = false;

        if (showEmailCapture) {
          // Email capture: position input in UPPER portion of viewport (for mobile keyboard visibility)
          // Target position: 1/3 down from top of viewport (leaves 2/3 below for keyboard + send button)
          const viewportHeight = containerRect.height;
          const targetPositionFromTop = viewportHeight * 0.33; // 1/3 down from top
          const currentPositionFromTop = elementRect.top - containerRect.top;
          const positionDifference = Math.abs(currentPositionFromTop - targetPositionFromTop);
          const isOptimallyPositioned = positionDifference < 50; // Within 50px of target

          console.log('👁️ Email capture visibility check:', {
            elementTop: elementRect.top,
            elementBottom: elementRect.bottom,
            containerTop: containerRect.top,
            containerHeight: viewportHeight.toFixed(2),
            targetPositionFromTop: targetPositionFromTop.toFixed(2),
            currentPositionFromTop: currentPositionFromTop.toFixed(2),
            positionDifference: positionDifference.toFixed(2),
            isOptimallyPositioned,
            strategy: '1/3-from-top-for-keyboard'
          });

          // Scroll if element is NOT at optimal position (1/3 from top)
          needsScroll = !isOptimallyPositioned;
        } else {
          // Answer section: standard visibility check (anywhere on screen is OK)
          const isVisible = elementRect.bottom <= containerRect.bottom &&
                           elementRect.top >= containerRect.top;

          console.log('👁️ Answer section visibility check:', {
            isVisible,
            elementBottom: elementRect.bottom,
            elementTop: elementRect.top,
            containerBottom: containerRect.bottom,
            containerTop: containerRect.top,
            pixelsOutOfView: elementRect.bottom - containerRect.bottom
          });

          needsScroll = !isVisible;
        }

        // Scroll if needed
        if (needsScroll) {
          let scrollTo;

          if (showEmailCapture) {
            // Email capture: scroll to position input at 1/3 from top of viewport
            const viewportHeight = containerRect.height;
            const targetPositionFromTop = viewportHeight * 0.33;
            const currentPositionFromTop = elementRect.top - containerRect.top;
            const scrollAdjustment = currentPositionFromTop - targetPositionFromTop;
            scrollTo = conversationArea.scrollTop + scrollAdjustment;

            console.log('📜 Auto-scroll (email capture):', {
              section: 'email-capture',
              targetElement: targetElement.className,
              targetPositionFromTop: targetPositionFromTop.toFixed(2),
              currentPositionFromTop: currentPositionFromTop.toFixed(2),
              scrollAdjustment: scrollAdjustment.toFixed(2),
              currentScroll: conversationArea.scrollTop.toFixed(2),
              newScroll: scrollTo.toFixed(2),
              strategy: 'position-at-1/3-from-top'
            });
          } else {
            // Answer sections: scroll to show bottom of element with 20px buffer
            const buffer = 20;
            scrollTo = conversationArea.scrollTop +
                      (elementRect.bottom - containerRect.bottom) + buffer;

            console.log('📜 Auto-scroll (answer section):', {
              section: 'answer-section',
              targetElement: targetElement.className,
              buffer,
              scrollTo,
              elementBottom: elementRect.bottom,
              containerBottom: containerRect.bottom
            });
          }

          conversationArea.scrollTo({
            top: Math.max(0, scrollTo),
            behavior: 'smooth'
          });
        } else {
          console.log('✅ Element optimally positioned, no scroll needed');
        }
      }
    }, 200);
  }, [conversationHistory, currentQuestionIndex, showEmailCapture, showTypingIndicator, emailError]);

  // Reset slider state when question changes
  useEffect(() => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion?.type === 'slider') {
      setSliderValue(0); // Reset to leftmost position
      setSliderTouched(false); // Reset touched state to force interaction
    }
  }, [currentQuestionIndex, questions]);

  // Drag handlers
  const handleDragStart = (e) => {
    console.log('Drag started!');
    setDragState({ isDragging: true, dragOverBucket: null });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', 'dragging');
  };

  const handleDragEnd = (e) => {
    console.log('Drag ended!');
    setDragState({ isDragging: false, dragOverBucket: null });
  };

  // Initialize user document and quiz session when user is authenticated
  useEffect(() => {
    const initializeSession = async () => {
      if (user && !sessionId) {
        try {
          await createUserDocument(user.uid);
          const newSessionId = await createQuizSession(user.uid);
          setSessionId(newSessionId);
          console.log('Session initialized:', newSessionId);

          // Track quiz started
          quizStartTime.current = Date.now();
          questionStartTime.current = Date.now();
          analyticsService.trackQuizStarted(newSessionId);
        } catch (error) {
          console.error('Error initializing session:', error);
        }
      }
    };
    initializeSession();
  }, [user, sessionId]);

  const fetchQuestions = async () => {
    try {
      const questionsRef = collection(db, 'template_questions');
      const q = query(questionsRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);

      const fetchedQuestions = [];
      querySnapshot.forEach((doc) => {
        fetchedQuestions.push({ id: doc.id, ...doc.data() });
      });

      setQuestions(fetchedQuestions);
      setLoading(false);

      // Add first question to conversation
      if (fetchedQuestions.length > 0) {
        setConversationHistory([
          {
            type: 'ai',
            text: "Hey there! I'm Jaimee - what's your name?"
          }
        ]);
        triggerHapticFeedback();
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setLoading(false);
    }
  };

  const handleAnswerSelect = (optionIndex) => {
    const currentQuestion = questions[currentQuestionIndex];

    if (currentQuestion.type === 'multi-select') {
      // Toggle selection for multi-select
      if (selectedAnswers.includes(optionIndex)) {
        setSelectedAnswers(selectedAnswers.filter(i => i !== optionIndex));
      } else {
        // Check if max_selection limit is reached
        const maxSelection = currentQuestion.max_selection;
        if (maxSelection && selectedAnswers.length >= maxSelection) {
          // Don't allow selecting more than max_selection
          return;
        }
        setSelectedAnswers([...selectedAnswers, optionIndex]);
      }
    } else {
      // Single select - send answer directly
      handleSendAnswer([optionIndex]);
    }
  };

  const handleFreeTextSubmit = () => {
    if (freeTextInput.trim() === '') return;

    // Blur input before submitting
    document.activeElement?.blur();

    handleSendAnswer(null, freeTextInput.trim());
  };

  // Handle Continue button click on "Did you know" cards
  const handleContinue = () => {
    if (!pendingNextQuestion.current) return;

    const { nextQuestion, historyWithCommentary } = pendingNextQuestion.current;

    setWaitingForContinue(false);
    setCurrentQuestionIndex(prev => prev + 1);
    setConversationHistory([
      ...historyWithCommentary,
      { type: 'ai', text: nextQuestion.question_text }
    ]);
    triggerHapticFeedback();
    setIsTransitioning(false); // Show answer panel with new options

    pendingNextQuestion.current = null;
  };

  const handleEmailSubmit = async () => {
    const email = emailInput.trim();

    if (email === '') {
      setEmailError('Please enter your email address.');
      return;
    }

    setIsValidatingEmail(true);
    setEmailError('');

    try {
      // Validate email (format + DNS check)
      const validation = await validateEmail(email);

      if (!validation.isValid) {
        setEmailError(validation.error);
        setIsValidatingEmail(false);
        // Track email validation failure
        analyticsService.trackEmailValidationFailed(validation.errorType || 'format');
        return;
      }

      console.log('✅ Email validated:', email);

      // Track email captured
      analyticsService.trackEmailCaptured({
        sessionId,
        email,
      });

      // Save email to Firestore
      if (user) {
        try {
          await updateUserEmail(user.uid, email);
          console.log('✅ Email saved to Firestore');
        } catch (error) {
          console.error('❌ Error saving email to Firestore:', error);
        }

        // Update email in backend via API
        try {
          const jwtToken = await user.getIdToken();
          await apiService.updateProfileAttribute(jwtToken, 'email', email);
          console.log('✅ Email updated in backend');

          // NOW submit quiz results to backend API (after email is captured)
          // This ensures the Cloud Function has the correct email when sending results
          try {
            console.log('📤 Submitting quiz results to API...');
            const chatMessages = await getChatMessages(sessionId);
            const quizResult = await apiService.submitQuizResults(
              jwtToken,
              sessionId,
              user.uid,
              userName,
              questions,
              chatMessages
            );
            console.log('✅ Quiz results submitted to API:', quizResult);
          } catch (quizError) {
            console.error('❌ Error submitting quiz results:', quizError);
            // Notify Slack about quiz API error
            notifyQuizApiError(userName || 'Anonymous', sessionId, quizError.message);
          }
        } catch (error) {
          console.error('❌ Error updating email in backend:', error);
        }
      }

      // Hide email capture form and show email as user message bubble
      setShowEmailCapture(false);
      setIsValidatingEmail(false);

      // Add user email as a chat bubble
      setConversationHistory(prev => [
        ...prev,
        { type: 'user', text: email }
      ]);
      triggerHapticFeedback();

      // Show typing indicator briefly, then redirect to results page
      setShowTypingIndicator(true);

      // Wait a bit, then redirect to the results page
      setTimeout(() => {
        console.log('🚀 Redirecting to results page:', `/results/${sessionId}`);
        navigate(`/results/${sessionId}`);
      }, 1500);

    } catch (error) {
      console.error('❌ Error in email submission:', error);
      setEmailError('An error occurred. Please try again.');
      setIsValidatingEmail(false);
    }
  };

  const handleSliderChange = (e) => {
    setSliderValue(parseInt(e.target.value));
    setSliderTouched(true); // Mark slider as touched when user interacts with it
  };

  const handleSliderLabelClick = (index) => {
    setSliderValue(index);
    setSliderTouched(true); // Mark slider as touched when user clicks a label
  };

  const handleSliderSubmit = () => {
    handleSendAnswer([sliderValue]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, bucketId) => {
    e.preventDefault();
    console.log('Drag enter bucket:', bucketId);
    setDragState((prev) => ({ ...prev, dragOverBucket: bucketId }));
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    console.log('Drag leave');
    setDragState((prev) => ({ ...prev, dragOverBucket: null }));
  };

  const handleDrop = (e, bucketId) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Dropped in bucket:', bucketId);

    const currentQuestion = questions[currentQuestionIndex];
    const currentItemIndex = bucketSort.currentItemIndex;

    // Add current item to selected bucket
    const newAssignments = {
      ...bucketSort.assignments,
      [bucketId]: [...(bucketSort.assignments[bucketId] || []), currentItemIndex],
    };

    setDragState({ isDragging: false, dragOverBucket: null });

    // Check if this was the last item
    if (currentItemIndex >= currentQuestion.options.length - 1) {
      // All items sorted - calculate score and submit
      let totalScore = 0;
      currentQuestion.buckets.forEach((bucket) => {
        const itemsInBucket = newAssignments[bucket.id] || [];
        totalScore += itemsInBucket.length * bucket.value;
      });

      // Send the answer with the total score
      // Note: We DON'T reset bucketSort here - let it stay at the last item
      handleSendAnswer([totalScore], null, newAssignments);
    } else {
      // Move to next item
      setBucketSort({
        currentItemIndex: currentItemIndex + 1,
        assignments: newAssignments,
      });
    }
  };

  const handleSendAnswer = async (answers = selectedAnswers, freeText = null, bucketAssignments = null) => {
    const currentQuestion = questions[currentQuestionIndex];

    // Handle different question types
    let userAnswers;
    let selectedOptions = null;

    if (currentQuestion.type === 'free-text') {
      if (!freeText || freeText.trim() === '') return;
      userAnswers = freeText;

      // If this is the name question (order 1), save as display_name
      if (currentQuestion.order === 1 && sessionId && user) {
        try {
          await updateUserDisplayName(user.uid, freeText);
          setUserName(freeText); // Store the name for future messages
          console.log('Display name saved:', freeText);
        } catch (error) {
          console.error('Error saving display name:', error);
        }
      }
    } else if (currentQuestion.type === 'bucket-sort') {
      if (!answers || answers.length === 0) return;
      // For bucket-sort, answers[0] is the total score
      userAnswers = 'Relationship needs assessment completed';
      // Convert bucket assignments from indices to option text
      selectedOptions = {};
      Object.keys(bucketAssignments).forEach(bucketId => {
        selectedOptions[bucketId] = bucketAssignments[bucketId].map(
          index => currentQuestion.options[index].option_text
        );
      });
    } else {
      if (!answers || answers.length === 0) return;
      userAnswers = answers.map(i => currentQuestion.options[i].option_text).join(', ');
      selectedOptions = answers;
    }

    // Add user answer to conversation
    const newHistory = [
      ...conversationHistory,
      { type: 'user', text: userAnswers }
    ];

    // Save user answer to Firestore
    if (sessionId && user) {
      try {
        const messageData = {
          type: 'user',
          message: userAnswers,
          senderName: userName,
          questionData: {
            id: currentQuestion.id,
            order: currentQuestion.order,
            type: currentQuestion.type,
          }
        };

        if (selectedOptions) {
          messageData.selectedOptions = selectedOptions;
        }

        await saveChatMessage(sessionId, user.uid, messageData);

        // Track question answered with time spent
        const timeSpentSeconds = questionStartTime.current
          ? Math.round((Date.now() - questionStartTime.current) / 1000)
          : 0;

        analyticsService.trackQuestionAnswered({
          sessionId,
          questionId: currentQuestion.id,
          questionOrder: currentQuestion.order,
          questionType: currentQuestion.type,
          timeSpentSeconds,
        });

        // Track quiz milestones (25%, 50%, 75%)
        const percentComplete = ((currentQuestionIndex + 1) / questions.length) * 100;
        const milestones = [
          { threshold: 25, label: '25%' },
          { threshold: 50, label: '50%' },
          { threshold: 75, label: '75%' },
        ];

        milestones.forEach(({ threshold, label }) => {
          if (percentComplete >= threshold && !milestonesReached.current[label]) {
            milestonesReached.current[label] = true;
            analyticsService.trackMilestoneReached({
              sessionId,
              milestone: label,
              questionOrder: currentQuestionIndex + 1,
            });
          }
        });

        // Reset question timer for next question
        questionStartTime.current = Date.now();
      } catch (error) {
        console.error('Error saving user answer:', error);
      }
    }

    // Handle grouped questions (like loneliness scale)
    let shouldShowCommentary = true;
    let commentaryText = null;
    let didYouKnowText = null;

    if (currentQuestion.type === 'bucket-sort' && currentQuestion.scoring_rules) {
      // Bucket-sort: use total score to find commentary
      const totalScore = answers[0]; // Total score passed from handleBucketSelect

      const scoringRule = currentQuestion.scoring_rules.commentary.find(
        rule => totalScore >= rule.min && totalScore <= rule.max
      );

      if (scoringRule) {
        commentaryText = scoringRule.text;
      }

      // Did you know for bucket-sort is in the question itself
      didYouKnowText = currentQuestion.did_you_know;
    } else if (currentQuestion.question_group) {
      // Store score for this grouped question
      const questionScore = currentQuestion.options[answers[0]].value;
      const updatedGroupScores = {
        ...groupedScores,
        [currentQuestion.question_group]: [
          ...(groupedScores[currentQuestion.question_group] || []),
          questionScore
        ]
      };
      setGroupedScores(updatedGroupScores);

      // Check if we should show commentary after this question
      shouldShowCommentary = currentQuestion.show_commentary_after === true;

      if (shouldShowCommentary && currentQuestion.scoring_rules) {
        // Calculate total score for the group
        const totalScore = updatedGroupScores[currentQuestion.question_group].reduce((sum, score) => sum + score, 0);

        // Find matching commentary based on score range
        const scoringRule = currentQuestion.scoring_rules.commentary.find(
          rule => totalScore >= rule.min && totalScore <= rule.max
        );

        if (scoringRule) {
          commentaryText = scoringRule.text;
          didYouKnowText = scoringRule.did_you_know;
        }
      }
    } else if (currentQuestion.type === 'free-text' && currentQuestion.commentary) {
      // Free-text question with commentary (e.g., personalized greeting after name)
      // Use the just-captured name (freeText) for replacement since userName state hasn't updated yet
      const displayName = freeText || userName || '';
      commentaryText = currentQuestion.commentary.replace('{display_name}', displayName);
    } else if (currentQuestion.type !== 'free-text' && currentQuestion.options.length > 0) {
      // Regular question - get commentary from selected option
      const selectedOption = currentQuestion.options[answers[0]];
      commentaryText = selectedOption.commentary;
      didYouKnowText = selectedOption.did_you_know;
    }

    // Add commentary if we should show it
    if (shouldShowCommentary && commentaryText) {
      newHistory.push({ type: 'ai', text: commentaryText });

      // Save AI commentary to Firestore
      if (sessionId && user) {
        try {
          await saveChatMessage(sessionId, user.uid, {
            type: 'ai',
            message: commentaryText,
            questionData: {
              id: currentQuestion.id,
              order: currentQuestion.order,
              type: currentQuestion.type,
            },
          });
        } catch (error) {
          console.error('Error saving AI commentary:', error);
        }
      }
    }

    // Add "did you know" content if it exists
    if (shouldShowCommentary && didYouKnowText) {
      newHistory.push({ type: 'did_you_know', text: didYouKnowText });

      // Save "did you know" to Firestore
      if (sessionId && user) {
        try {
          await saveChatMessage(sessionId, user.uid, {
            type: 'ai',
            message: didYouKnowText,
            messageType: 'did_you_know',
            questionData: {
              id: currentQuestion.id,
              order: currentQuestion.order,
              type: currentQuestion.type,
            },
          });
        } catch (error) {
          console.error('Error saving did_you_know:', error);
        }
      }
    }

    // SAVE scroll position for bucket-sort (to restore after layout changes)
    const conversationArea = document.querySelector('.conversation-area');
    if (shouldShowCommentary && currentQuestion.type === 'bucket-sort' && conversationArea) {
      savedScrollPosition.current = conversationArea.scrollTop;
      console.log('💾 Saved scroll position for bucket-sort:', savedScrollPosition.current);
    }

    // Clear inputs immediately
    setSelectedAnswers([]);
    setFreeTextInput('');

    // ============================================================
    // SIMPLE PACED FLOW: Each step shows message, scrolls, then delays
    // 1) Show answer & scroll → 600ms delay
    // 2) Show commentary & scroll → 600ms delay
    // 3) Show next question & scroll
    // ============================================================

    // Hide answer panel IMMEDIATELY when answer is selected
    setIsTransitioning(true);

    // STEP 1: Show user answer bubble
    const historyWithAnswer = [
      ...conversationHistory,
      { type: 'user', text: userAnswers }
    ];
    setConversationHistory(historyWithAnswer);
    triggerHapticFeedback();

    // Check if this is the final question
    if (currentQuestion.is_final_question) {
      // Immediately and completely hide the answer panel for the final question flow
      setHidePanelForFinal(true);

      // STEP 1: Show commentary first (if exists) after 600ms
      const hasCommentary = commentaryText && commentaryText.trim().length > 0;

      if (hasCommentary) {
        // Show commentary first
        setTimeout(() => {
          const historyWithCommentary = [
            ...historyWithAnswer,
            { type: 'ai', text: commentaryText }
          ];
          setConversationHistory(historyWithCommentary);
          triggerHapticFeedback();

          // STEP 2: After 1200ms delay (to read commentary), show final_message
          setTimeout(async () => {
            const finalMessage = currentQuestion.final_message?.replace('{display_name}', userName) ||
                                 "Thanks so much for sharing. Give me a moment to reflect on everything you've told me.";
            setConversationHistory(prev => [
              ...prev,
              { type: 'ai', text: finalMessage }
            ]);
            triggerHapticFeedback();

            // Show typing indicator for processing
            setShowTypingIndicator(true);

            // Mark session as complete in Firestore and submit to backend API
            if (sessionId && user) {
              // Track quiz completed
              const totalTimeSeconds = quizStartTime.current
                ? Math.round((Date.now() - quizStartTime.current) / 1000)
                : 0;
              analyticsService.trackQuizCompleted({
                sessionId,
                totalTimeSeconds,
                questionsAnswered: questions.length,
              });

              let jwtToken;
              try {
                jwtToken = await user.getIdToken();
                const profileResponse = await apiService.createProfile(jwtToken);
                console.log('✅ Profile created:', profileResponse);
              } catch (error) {
                console.error('❌ Error creating profile:', error);
                notifyProfileCreationError(userName || 'Anonymous', user?.uid, sessionId, error.message);
              }

              completeQuizSession(sessionId)
                .then(() => {
                  console.log('✅ Quiz completed in Firestore!');
                  setShowTypingIndicator(false);
                  setIsTransitioning(false);
                  setConversationHistory(prev => [
                    ...prev,
                    { type: 'ai', text: 'Amazing! Now, enter your email to see your personalized results.' }
                  ]);
                  triggerHapticFeedback();
                  setShowEmailCapture(true);
                })
                .catch(error => {
                  console.error('Error completing quiz session:', error);
                  setShowTypingIndicator(false);
                  setIsTransitioning(false);
                  setConversationHistory(prev => [
                    ...prev,
                    { type: 'ai', text: 'Amazing! Now, enter your email to see your personalized results.' }
                  ]);
                  triggerHapticFeedback();
                  setShowEmailCapture(true);
                  notifyGeneralError('Quiz session completion', userName || 'Anonymous', sessionId, error.message);
                });
            }
          }, 1200);
        }, 600);
      } else {
        // No commentary - go straight to final_message
        setTimeout(async () => {
          const finalMessage = currentQuestion.final_message?.replace('{display_name}', userName) ||
                               "Thanks so much for sharing. Give me a moment to reflect on everything you've told me.";
          setConversationHistory(prev => [
            ...prev,
            { type: 'ai', text: finalMessage }
          ]);
          triggerHapticFeedback();

          // Show typing indicator for processing
          setShowTypingIndicator(true);

          // Mark session as complete in Firestore and submit to backend API
          if (sessionId && user) {
            // Track quiz completed
            const totalTimeSeconds = quizStartTime.current
              ? Math.round((Date.now() - quizStartTime.current) / 1000)
              : 0;
            analyticsService.trackQuizCompleted({
              sessionId,
              totalTimeSeconds,
              questionsAnswered: questions.length,
            });

            let jwtToken;
            try {
              jwtToken = await user.getIdToken();
              const profileResponse = await apiService.createProfile(jwtToken);
              console.log('✅ Profile created:', profileResponse);
            } catch (error) {
              console.error('❌ Error creating profile:', error);
              notifyProfileCreationError(userName || 'Anonymous', user?.uid, sessionId, error.message);
            }

            completeQuizSession(sessionId)
              .then(() => {
                console.log('✅ Quiz completed in Firestore!');
                setShowTypingIndicator(false);
                setIsTransitioning(false);
                setConversationHistory(prev => [
                  ...prev,
                  { type: 'ai', text: 'Amazing! Now, enter your email to see your personalized results.' }
                ]);
                triggerHapticFeedback();
                setShowEmailCapture(true);
              })
              .catch(error => {
                console.error('Error completing quiz session:', error);
                setShowTypingIndicator(false);
                setIsTransitioning(false);
                setConversationHistory(prev => [
                  ...prev,
                  { type: 'ai', text: 'Amazing! Now, enter your email to see your personalized results.' }
                ]);
                triggerHapticFeedback();
                setShowEmailCapture(true);
                notifyGeneralError('Quiz session completion', userName || 'Anonymous', sessionId, error.message);
              });
          }
        }, 600);
      }
      return;
    }

    // For non-final questions with commentary (also allow free-text with commentary)
    const showCommentaryForFreeText = currentQuestion.type === 'free-text' && currentQuestion.commentary;
    // Show commentary if: (1) show_commentary_after is true, OR (2) it's a free-text with commentary
    if (commentaryText && (shouldShowCommentary || showCommentaryForFreeText)) {
      // STEP 2: After 600ms, show commentary
      setTimeout(() => {
        const historyWithCommentary = [
          ...historyWithAnswer,
          { type: 'ai', text: commentaryText }
        ];
        setConversationHistory(historyWithCommentary);
        triggerHapticFeedback();

        // If there's a "Did you know", show it after 1200ms delay (to allow reading commentary)
        if (didYouKnowText && currentQuestionIndex < questions.length - 1) {
          setTimeout(() => {
            const historyWithDidYouKnow = [
              ...historyWithCommentary,
              { type: 'did_you_know', text: didYouKnowText }
            ];
            setConversationHistory(historyWithDidYouKnow);
            triggerHapticFeedback();

            const nextQuestion = questions[currentQuestionIndex + 1];
            pendingNextQuestion.current = { nextQuestion, historyWithCommentary: historyWithDidYouKnow };
            setWaitingForContinue(true);
          }, 1200);
        } else {
          // STEP 3: After 1200ms, show next question and SHOW answer panel
          // (longer delay to allow reading commentary)
          setTimeout(() => {
            if (currentQuestionIndex < questions.length - 1) {
              const nextQuestion = questions[currentQuestionIndex + 1];
              setCurrentQuestionIndex(currentQuestionIndex + 1);
              setConversationHistory([
                ...historyWithCommentary,
                { type: 'ai', text: nextQuestion.question_text }
              ]);
              triggerHapticFeedback();
              setIsTransitioning(false); // Show answer panel with new options
            }
          }, 1200);
        }
      }, 600);

    } else {
      // No commentary - just show next question after 600ms
      setTimeout(() => {
        if (currentQuestionIndex < questions.length - 1) {
          const nextQuestion = questions[currentQuestionIndex + 1];
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          setConversationHistory([
            ...historyWithAnswer,
            { type: 'ai', text: nextQuestion.question_text }
          ]);
          triggerHapticFeedback();
          setIsTransitioning(false); // Show answer panel with new options
        }
      }, 600);
    }
  };

  // Touch handlers for mobile drag & drop support (wrapped in useCallback for stable references)
  const handleTouchStart = useCallback((e) => {
    e.preventDefault(); // Prevent default browser touch behavior
    console.log('Touch started!');
    const touch = e.touches[0];
    setTouchState({
      isTouching: true,
      touchStartX: touch.clientX,
      touchStartY: touch.clientY,
      currentX: touch.clientX,
      currentY: touch.clientY,
      currentBucket: null,
    });
    setDragState({ isDragging: true, dragOverBucket: null });
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault(); // Prevent scrolling while dragging
    const touch = e.touches[0];

    // Update touch position for visual feedback
    setTouchState(prev => ({
      ...prev,
      currentX: touch.clientX,
      currentY: touch.clientY
    }));

    const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY);

    // Find the bucket element (traverse up to find bucket-drop-zone)
    let bucketElement = elementAtPoint;
    while (bucketElement && !bucketElement.classList.contains('bucket-drop-zone')) {
      bucketElement = bucketElement.parentElement;
    }

    if (bucketElement) {
      // Get the bucket ID from the data attribute or from the buckets array by index
      const buckets = document.querySelectorAll('.bucket-drop-zone');
      const bucketIndex = Array.from(buckets).indexOf(bucketElement);

      if (bucketIndex !== -1) {
        const currentQuestion = questions[currentQuestionIndex];
        const bucketId = currentQuestion.buckets[bucketIndex].id;

        console.log('Touch over bucket:', bucketId);
        setTouchState(prev => ({ ...prev, currentBucket: bucketId }));
        setDragState(prev => ({ ...prev, dragOverBucket: bucketId }));
      }
    } else {
      // Not over any bucket
      setTouchState(prev => ({ ...prev, currentBucket: null }));
      setDragState(prev => ({ ...prev, dragOverBucket: null }));
    }
  }, [questions, currentQuestionIndex]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault(); // Prevent default touch end behavior
    console.log('Touch ended!');

    setTouchState(prevTouch => {
      const bucketId = prevTouch.currentBucket;
      console.log('Touch end - bucketId:', bucketId, 'isTouching:', prevTouch.isTouching);

      if (bucketId && prevTouch.isTouching) {
        console.log('Dropped in bucket:', bucketId);

        // Get current question and item index
        const currentQuestion = questions[currentQuestionIndex];
        const currentItemIndex = bucketSort.currentItemIndex;

        // Add current item to selected bucket
        const newAssignments = {
          ...bucketSort.assignments,
          [bucketId]: [...(bucketSort.assignments[bucketId] || []), currentItemIndex],
        };

        // Set dropping state for animation
        setDragState({ isDragging: false, dragOverBucket: null, isDropping: true });

        // Wait for drop animation to complete (300ms) before showing next card
        setTimeout(() => {
          // Reset dropping state first
          setDragState({ isDragging: false, dragOverBucket: null, isDropping: false });

          // Check if this was the last item
          if (currentItemIndex >= currentQuestion.options.length - 1) {
            // All items sorted - calculate score and submit
            let totalScore = 0;
            currentQuestion.buckets.forEach((bucket) => {
              const itemsInBucket = newAssignments[bucket.id] || [];
              totalScore += itemsInBucket.length * bucket.value;
            });

            // Send the answer with the total score
            // Note: We DON'T reset bucketSort here - let it stay at the last item
            handleSendAnswer([totalScore], null, newAssignments);
          } else {
            // Move to next item
            console.log('Moving to next item:', currentItemIndex + 1);
            setBucketSort({
              currentItemIndex: currentItemIndex + 1,
              assignments: newAssignments,
            });
          }
        }, 300); // Match animation duration
      } else {
        // Touch ended but not over a bucket - just reset state
        console.log('Touch ended without drop - resetting state');
        setDragState({ isDragging: false, dragOverBucket: null, isDropping: false });
      }

      return {
        isTouching: false,
        touchStartX: null,
        touchStartY: null,
        currentX: null,
        currentY: null,
        currentBucket: null,
      };
    });
  }, [questions, currentQuestionIndex, bucketSort, handleSendAnswer]);

  // Attach native touch listeners to dragItem (bypasses React's passive event system)
  // MUST come after touch handler definitions to avoid "Cannot access before initialization" error
  useEffect(() => {
    const dragItem = dragItemRef.current;
    if (!dragItem) return;

    // Attach native touch event listeners with { passive: false } to allow preventDefault()
    dragItem.addEventListener('touchstart', handleTouchStart, { passive: false });
    dragItem.addEventListener('touchmove', handleTouchMove, { passive: false });
    dragItem.addEventListener('touchend', handleTouchEnd, { passive: false });

    // Cleanup on unmount
    return () => {
      dragItem.removeEventListener('touchstart', handleTouchStart);
      dragItem.removeEventListener('touchmove', handleTouchMove);
      dragItem.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, currentQuestionIndex]);

  if (authLoading || loading) {
    return (
      <div className="quiz-container">
        <div className="quiz-loading-container">
          <img
            src="/assets/images/jaimee-logo.svg"
            alt="Jaimee"
            className="quiz-loading-logo"
          />
          <div className="quiz-loading-text">Loading the quiz...</div>
          <div className="quiz-loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="quiz-container">
        <div className="error">Authentication error: {authError}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="quiz-container">
        <div className="error">Unable to sign in. Please refresh the page.</div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="quiz-container">
        <div className="error">No questions found. Please add questions to Firestore.</div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className={`quiz-container ${currentQuestion.type === 'bucket-sort' ? 'bucket-sort-mode' : ''}`}>
      {/* Header with Jaimee branding - collapses when space is limited */}
      <div className={`quiz-header ${shouldCollapseHeader ? 'collapsed' : ''}`}>
        <div className="jaimee-avatar">
          <div style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            overflow: 'hidden'
          }}>
            <Lottie
              animationData={null}
              path="/assets/lottie/Jaimee S_think.json"
              loop={true}
              autoplay={true}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </div>
        <div className="logo-progress-container">
          <img
            src="/assets/images/jaimee-logo.svg"
            alt="Jaimee"
            className="jaimee-logo-svg"
          />
          {/* Progress indicator - line showing quiz completion percentage */}
          <div className="quiz-progress-line">
            <div
              className="quiz-progress-fill"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Conversation Area */}
      <div className={`conversation-area ${shouldCollapseHeader ? 'collapsed' : ''}`}>
        {conversationHistory.map((message, index) => {
          // Render "Did you know" as an info card
          if (message.type === 'did_you_know') {
            return (
              <div key={index} className="did-you-know-card">
                <div className="did-you-know-heading">Did you know?</div>
                <div className="did-you-know-content">{message.text}</div>
              </div>
            );
          }

          // Render regular chat messages
          return (
            <div
              key={index}
              className={`message ${message.type === 'ai' ? 'message-ai' : 'message-user'}`}
            >
              <div className="message-bubble">
                {message.text}
              </div>
            </div>
          );
        })}

        {/* Typing Indicator - Show when Jaimee is typing */}
        {showTypingIndicator && <TypingIndicator />}

        {/* Email Capture Form - Show after quiz completion */}
        {showEmailCapture && (
          <div className="email-capture-container">
            <div className="email-error" style={{
              color: '#D32F2F',
              fontSize: '13px',
              marginBottom: emailError ? '8px' : '0',
              textAlign: 'center',
              visibility: emailError ? 'visible' : 'hidden'
            }}>
              {emailError || 'Placeholder'}
            </div>
            <div className="free-text-input-container">
              <input
                type="email"
                className="free-text-input"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setEmailError(''); // Clear error on input change
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && emailInput.trim() !== '' && !isValidatingEmail) {
                    handleEmailSubmit();
                  }
                }}
                placeholder="Enter your email address..."
                autoFocus
                disabled={isValidatingEmail}
              />
              <button
                className="send-button"
                onClick={handleEmailSubmit}
                disabled={emailInput.trim() === '' || isValidatingEmail}
              >
                {isValidatingEmail ? 'Validating...' : 'Send'}
              </button>
            </div>
          </div>
        )}

        {/* Answer Section - Panel stays visible, contents hidden during transitions */}
        <div ref={answerSectionRef} className={`answer-section ${
          showEmailCapture || hidePanelForFinal ? 'answer-section-hidden-email' : ''
        } ${isTransitioning && !waitingForContinue ? 'answer-section-transitioning' : ''
        } ${answerSectionScroll.hasScroll ? 'has-scroll' : ''
        } ${answerSectionScroll.atBottom ? 'scrolled-to-bottom' : ''
        } ${currentQuestion.type === 'free-text' ? 'free-text-mode' : ''}`}>
          {waitingForContinue ? (
            // Show Continue button when waiting on Did You Know card
            <button className="continue-button" onClick={handleContinue}>
              Continue
            </button>
          ) : currentQuestion.type === 'bucket-sort' ? (
            <div className="bucket-sort-container">
              {/* Heading */}
              <div className="bucket-sort-heading">Drag each need into a bucket</div>

              {/* Buckets */}
              <div className="buckets-container">
                {currentQuestion.buckets.map((bucket) => {
                  const count = (bucketSort.assignments[bucket.id] || []).length;
                  const isDragOver = dragState.dragOverBucket === bucket.id;
                  const hasItems = count > 0;
                  return (
                    <div
                      key={bucket.id}
                      className={`bucket-drop-zone ${isDragOver ? 'drag-over' : ''} ${hasItems ? 'has-items' : ''}`}
                      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/bucket.svg)` }}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, bucket.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, bucket.id)}
                    >
                      {/* Counter badge at top (water area) */}
                      <div className="bucket-count-badge">
                        <span className="bucket-count">{count}</span>
                      </div>
                      {/* Centered content in bucket body */}
                      <div className="bucket-content">
                        <div className="bucket-emoji">{bucket.emoji}</div>
                        <div className="bucket-label">{bucket.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Progress indicator */}
              <div className="bucket-sort-progress">
                {bucketSort.currentItemIndex + 1} / {currentQuestion.options.length}
              </div>

              {/* Draggable item */}
              <div
                ref={dragItemRef}
                className={`bucket-sort-item ${dragState.isDragging ? 'dragging' : ''} ${dragState.isDropping ? 'dropping' : ''}`}
                draggable={!isTouchDevice} // Disable HTML5 drag on touch devices
                onDragStart={!isTouchDevice ? handleDragStart : undefined}
                onDragEnd={!isTouchDevice ? handleDragEnd : undefined}
                style={{
                  transform: touchState.isTouching && touchState.currentX !== null && touchState.currentY !== null
                    ? `translate(${touchState.currentX - touchState.touchStartX}px, ${touchState.currentY - touchState.touchStartY}px)`
                    : 'none',
                  transition: touchState.isTouching ? 'none' : 'transform 0.2s ease',
                  pointerEvents: touchState.isTouching ? 'none' : 'auto' // Allow touch to pass through while dragging
                }}
              >
                <div className="bucket-sort-item-text">
                  {currentQuestion.options[bucketSort.currentItemIndex].option_text}
                </div>
              </div>
            </div>
          ) : currentQuestion.type === 'slider' ? (
            // Slider input
            <div className="slider-container">
              <div className="slider-labels">
                {currentQuestion.options.map((option, index) => (
                  <div
                    key={index}
                    className="slider-label-item"
                    onClick={() => handleSliderLabelClick(index)}
                  >
                    <div className="slider-emoji">{option.emoji}</div>
                    <div className="slider-text">{option.option_text}</div>
                  </div>
                ))}
              </div>
              <input
                type="range"
                min="0"
                max={currentQuestion.options.length - 1}
                value={sliderValue}
                onChange={handleSliderChange}
                className={`slider-input ${!sliderTouched ? 'slider-untouched' : ''}`}
                step="1"
                style={{
                  '--slider-progress': `${(sliderValue / (currentQuestion.options.length - 1)) * 100}%`
                }}
              />
              <button
                className="send-button"
                onClick={handleSliderSubmit}
                disabled={!sliderTouched}
              >
                Send
              </button>
            </div>
          ) : currentQuestion.type === 'free-text' ? (
            // Free-text input
            <div className="free-text-input-container">
              <input
                type="text"
                className="free-text-input"
                value={freeTextInput}
                onChange={(e) => setFreeTextInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && freeTextInput.trim() !== '') {
                    handleFreeTextSubmit();
                  }
                }}
                placeholder={currentQuestionIndex === 0 ? "Enter your name" : "Type your answer..."}
                autoFocus
              />
              <button
                className="send-button"
                onClick={handleFreeTextSubmit}
                disabled={freeTextInput.trim() === ''}
              >
                Send
              </button>
            </div>
          ) : (
            // Multiple choice options
            <>
              {currentQuestion.type === 'multi-select' && (
                <div className="multi-select-label">
                  {currentQuestion.max_selection
                    ? `Select up to ${currentQuestion.max_selection}`
                    : 'Select all that apply'}
                </div>
              )}

              <div className={`options-list ${currentQuestion.type === 'image' ? 'image-options' : ''}`}>
                {currentQuestion.options
                  .filter(opt => opt.option_text && opt.option_text.trim() !== '')
                  .map((option, index) => (
                    <label
                      key={index}
                      className={`option-item ${currentQuestion.type === 'image' ? 'image-option' : ''}`}
                    >
                      <input
                        type={currentQuestion.type === 'multi-select' ? 'checkbox' : 'radio'}
                        name={`question-${currentQuestion.id}`}
                        checked={selectedAnswers.includes(index)}
                        onChange={() => handleAnswerSelect(index)}
                      />
                      {currentQuestion.type === 'image' && option.image_url && (
                        <img
                          src={option.image_url}
                          alt={option.option_text}
                          className="option-image"
                        />
                      )}
                      <span className="option-text">{option.option_text}</span>
                    </label>
                  ))}
              </div>

              {currentQuestion.type === 'multi-select' && (
                <button
                  className="send-button"
                  onClick={() => handleSendAnswer()}
                  disabled={selectedAnswers.length === 0}
                >
                  Send
                </button>
              )}
            </>
          )}
          </div>

        {/* Scroll indicators - OUTSIDE answer-section to avoid overflow clipping */}
        {answerSectionScroll.hasScroll && !answerSectionScroll.atBottom &&
         currentQuestion.type !== 'free-text' && !showEmailCapture && !hidePanelForFinal &&
         !isTransitioning && (
          <div className="answer-scroll-indicators">
            <div className="answer-scroll-fade" />
            <div className="answer-scroll-glow" />
          </div>
        )}
      </div>
    </div>
  );
}

export default QuizPage;
